import { useState } from 'react';

/** A typed number that is non-negative, or `null` for an empty or unreadable field. */
export const parseTypedNumber = (raw: string): number | null => {
    const parsed = Number(raw);
    return raw.trim() === '' || !Number.isFinite(parsed) || parsed < 0 ? null : parsed;
};

export interface TypedNumberChange {
    /** Exactly what the reader typed, kept in the field as-is. */
    raw: string;
    /** How the field's value will read once the change has been taken in. */
    nextValueText: string;
}

/**
 * Keeps a numeric field showing exactly what was typed ("12.", "5.20") while the number itself lives
 * elsewhere. The text this field produced is remembered, so only a value arriving from somewhere
 * else (a recomputation, "Annuler", an import) replaces what the reader is typing.
 */
export const useTypedNumber = (valueText: string) => {
    const [typed, setTyped] = useState(valueText);
    const [adoptedText, setAdoptedText] = useState(valueText);

    if (valueText !== adoptedText) {
        setAdoptedText(valueText);
        setTyped(valueText);
    }

    const type = ({ raw, nextValueText }: TypedNumberChange) => {
        setAdoptedText(nextValueText);
        setTyped(raw);
    };

    return [typed, type] as const;
};
